/**
 * This file is a part of KingLiving's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useMemo} from 'react';
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * Custom hook to memoize breakpoint values and prevent unnecessary re-renders
 *
 * @param {Object} breakpointConfig - Breakpoint configuration object
 * @returns {boolean} Memoized breakpoint value
 *
 * @example
 * const isMobile = useMemoizedBreakpoint({base: true, md: false});
 * const isDesktop = useMemoizedBreakpoint({base: false, lg: true});
 */
export function useMemoizedBreakpoint(breakpointConfig) {
  const breakpointValue = useBreakpointValue(breakpointConfig);

  // Memoize the value to prevent unnecessary re-renders
  // Only recalculate when the actual breakpoint value changes
  return useMemo(() => breakpointValue, [breakpointValue]);
}

/**
 * Predefined breakpoint hooks for common use cases
 */
export const useIsMobile = () => useMemoizedBreakpoint({base: true, md: false});
export const useIsDesktop = () => useMemoizedBreakpoint({base: false, md: true});
export const useIsTablet = () => useMemoizedBreakpoint({base: false, md: true, lg: false});
export const useIsLargeScreen = () => useMemoizedBreakpoint({base: false, lg: true});
